import { useState } from 'react'
import { Copy, Plus } from 'lucide-react'

import { createBusinessResponse, type CreateBusinessRequest } from '@cattle/contracts'

import { useIsPlatformOwner } from '../../app/session'
import {
  Button,
  Field,
  Modal,
  Select,
  TextInput,
  useToast,
} from '../../components'
import { queryKeys, usePlans, type PlanCode } from '../../fixtures'
import { post } from '../../lib/apiClient'
import { t } from '../../lib/i18n'
import { useApiMutation } from '../../lib/mutations'

/**
 * "Add a business" — the form, the write, and the one-time password.
 *
 * It lives here rather than on P-02 because P-01 offers the same button. Two
 * copies of a create form is how the two drift: one gains a field, the other
 * keeps sending a request the server has stopped accepting, and the difference
 * is only found by the trader whose book was created from the stale one.
 *
 * Every rule the P-02 version carried is kept:
 *
 * - **PLATFORM_OWNER only, and gated by ABSENCE.** Both the trigger and the
 *   dialog render nothing for a support account. A disabled button still
 *   advertises a power the reader does not have.
 * - **TEMPORARY: `ownerPassword` is sent, prefilled with `DEFAULT_OWNER_PASSWORD`.**
 *   The owner asked for a known first password while the platform is being
 *   tried out (11 Sept 2026). The design is that the server generates one and
 *   returns it once, so the console never holds a password it chose — clearing
 *   the field restores exactly that. Remove the default before real traders.
 * - Whatever the password, the sign-in details land in a follow-up modal with
 *   a Copy button: the business code is the one thing the trader cannot guess,
 *   and a toast would take it away while the reader is still looking for a pen.
 */

/**
 * P3's response.
 *
 * The contract exports the schema but no type beside it, so the type is taken
 * off the schema rather than restated — a restated shape is a second copy of
 * the contract.
 *
 * CONTRACT GAP: `createBusinessResponse` (and `personSchema` under it) does not
 * declare `oneTimePassword`, although P3 returns it. It is read off the raw
 * response as an optional string rather than parsed, because parsing against
 * the published schema would strip the one field this dialog exists to show.
 */
type CreateBusinessResponse = ReturnType<typeof createBusinessResponse.parse> & {
  oneTimePassword?: string
}

/** `usernameSchema` in the contract. Refused server-side too; both stay. */
const USERNAME = /^[a-z0-9._+-]{3,40}$/i

/** Blank means "let the server generate one". See the header — temporary. */
const DEFAULT_OWNER_PASSWORD = '12345678'

/** The owner's sign-in details, held only long enough to be read once. */
interface FirstPassword {
  business: string
  slug: string
  owner: string
  username: string
  password: string
}

/**
 * The trigger. Renders nothing at all for anyone but the platform owner, which
 * is the same gate the dialog itself applies — stated twice because the button
 * and the form are placed by different screens.
 */
export function AddBusinessButton({ onClick }: { onClick: () => void }) {
  const isOwner = useIsPlatformOwner()
  if (!isOwner) return null

  return (
    <Button variant="primary" icon={<Plus className="size-4" />} onClick={onClick}>
      {t('p.overview.addBusiness')}
    </Button>
  )
}

export interface AddBusinessDialogProps {
  open: boolean
  /** Called on cancel and after a successful create. The screen owns the flag. */
  onClose: () => void
}

export function AddBusinessDialog({ open, onClose }: AddBusinessDialogProps) {
  const isOwner = useIsPlatformOwner()
  const plans = usePlans()
  const toast = useToast()

  const [name, setName] = useState('')
  const [city, setCity] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [ownerUsername, setOwnerUsername] = useState('')
  const [ownerPhone, setOwnerPhone] = useState('')
  const [planCode, setPlanCode] = useState<PlanCode | null>(null)
  const [trialDays, setTrialDays] = useState('')
  const [ownerPassword, setOwnerPassword] = useState(DEFAULT_OWNER_PASSWORD)
  const [firstPassword, setFirstPassword] = useState<FirstPassword | null>(null)

  const closeAdd = () => {
    onClose()
    setName('')
    setCity('')
    setOwnerName('')
    setOwnerUsername('')
    setOwnerPhone('')
    setPlanCode(null)
    setTrialDays('')
    setOwnerPassword(DEFAULT_OWNER_PASSWORD)
  }

  /**
   * P3 · a book created on someone's behalf, for a trader who signed up over
   * the phone. It is one transaction on the server — tenant, owner,
   * subscription, feed plan and settings — so there is one call here.
   */
  const addBusiness = useApiMutation<CreateBusinessRequest, CreateBusinessResponse>({
    run: (body) => post<CreateBusinessResponse>('/platform/businesses', body),
    // A new book is a new subscription, a new row in every count, and storage
    // against a plan limit.
    invalidates: [
      queryKeys.businesses,
      queryKeys.platformOverview,
      queryKeys.subscriptions,
      queryKeys.usage,
    ],
    success: (result) => t('p.businesses.created', { name: result.business.name }),
    onDone: (result, body) => {
      closeAdd()
      // A toast would take it away while the reader is still looking for a pen.
      const password = result.oneTimePassword ?? body.ownerPassword
      if (password) {
        setFirstPassword({
          business: result.business.name,
          slug: result.business.slug,
          owner: result.business.ownerName,
          username: body.ownerUsername,
          password,
        })
      }
    },
  })

  /** 0–365 per `createBusinessRequest`. Blank means "the plan's own length". */
  const parsedTrialDays = Number(trialDays.trim())
  const trialDaysOk =
    trialDays.trim() === '' ||
    (Number.isInteger(parsedTrialDays) && parsedTrialDays >= 0 && parsedTrialDays <= 365)

  // The same bounds the contract states. The API refuses independently; this
  // only stops a request that is already known to be refused.
  const usernameOk = USERNAME.test(ownerUsername.trim())
  // 8–128 per `passwordSchema`, or blank for a server-generated one.
  const passwordOk = ownerPassword === '' || (ownerPassword.length >= 8 && ownerPassword.length <= 128)

  /**
   * The plans the platform actually sells, from P-05 — NOT `PLAN_CODES`.
   *
   * `PLAN_CODES` is the contract's closed set of four; a plan exists only once
   * someone has priced it on the Plans screen. Offering the constant is how a
   * fresh deployment ends up sending `planCode: "TRIAL"` at an empty `plans`
   * table and getting "There is no plan with that code." back from a form that
   * looked correctly filled in.
   *
   * Nothing chosen yet means the first plan on the list, which `usePlans()`
   * sorts cheapest first. Derived rather than pushed into state by an effect,
   * so it cannot be a plan that has since been renamed away.
   */
  const sellablePlans = plans.data ?? []
  const chosenPlan =
    sellablePlans.find((plan) => plan.code === planCode)?.code ?? sellablePlans[0]?.code ?? null

  const canAdd =
    chosenPlan !== null &&
    name.trim().length >= 2 &&
    ownerName.trim().length >= 2 &&
    usernameOk &&
    passwordOk &&
    trialDaysOk &&
    !addBusiness.isPending

  const submitAdd = () => {
    if (chosenPlan === null) return
    addBusiness.mutate({
      name: name.trim(),
      city: city.trim() === '' ? undefined : city.trim(),
      ownerName: ownerName.trim(),
      ownerUsername: ownerUsername.trim(),
      ownerPhone: ownerPhone.trim() === '' ? undefined : ownerPhone.trim(),
      ownerPassword: ownerPassword === '' ? undefined : ownerPassword,
      planCode: chosenPlan,
      trialDays: trialDays.trim() === '' ? undefined : parsedTrialDays,
    })
  }

  const copyPassword = () => {
    if (!firstPassword) return
    const details = [
      `${t('p.businesses.signInBusiness')}: ${firstPassword.slug}`,
      `${t('label.username')}: ${firstPassword.username}`,
      `${t('auth.password')}: ${firstPassword.password}`,
    ].join('\n')
    void navigator.clipboard.writeText(details).then(
      () => toast.show(t('p.businesses.passwordCopied')),
      () => toast.show(t('state.errorTitle'), 'danger'),
    )
  }

  // P3 is PLATFORM_OWNER. The form does not exist for anyone else — the hooks
  // above run first so this stays a render decision, not a conditional hook.
  if (!isOwner) return null

  return (
    <>
      <Modal
        open={open}
        onClose={closeAdd}
        title={t('p.overview.addBusiness')}
        subtitle={t('p.businesses.addSubtitle')}
        footer={
          <>
            <Button variant="ghost" onClick={closeAdd}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={!canAdd} onClick={submitAdd}>
              {t('p.overview.addBusiness')}
            </Button>
          </>
        }
      >
        <Field label={t('label.business')} htmlFor="new-business-name" required>
          <TextInput id="new-business-name" value={name} onChange={setName} />
        </Field>
        <Field label={t('p.businesses.cityLabel')} htmlFor="new-business-city">
          <TextInput id="new-business-city" value={city} onChange={setCity} />
        </Field>
        <Field label={t('p.businesses.ownerNameLabel')} htmlFor="new-business-owner" required>
          <TextInput id="new-business-owner" value={ownerName} onChange={setOwnerName} />
        </Field>
        <Field
          label={t('label.username')}
          hint={t('p.businesses.usernameHint')}
          htmlFor="new-business-username"
          error={
            ownerUsername.trim() !== '' && !usernameOk ? t('p.businesses.usernameInvalid') : undefined
          }
          required
        >
          <TextInput id="new-business-username" value={ownerUsername} onChange={setOwnerUsername} />
        </Field>
        <Field
          label={t('p.businesses.ownerPasswordLabel')}
          hint={t('p.businesses.ownerPasswordHint')}
          error={passwordOk ? undefined : t('p.businesses.ownerPasswordInvalid')}
          htmlFor="new-business-password"
        >
          <TextInput id="new-business-password" value={ownerPassword} onChange={setOwnerPassword} />
        </Field>
        <Field label={t('p.businesses.ownerPhoneLabel')} htmlFor="new-business-phone">
          <TextInput id="new-business-phone" value={ownerPhone} onChange={setOwnerPhone} />
        </Field>
        {/* A plan name is the platform owner's own wording (P-05), so it is data. */}
        <Field
          label={t('label.plan')}
          htmlFor="new-business-plan"
          error={
            !plans.isPending && sellablePlans.length === 0 ? t('p.businesses.noPlans') : undefined
          }
          required
        >
          {chosenPlan === null ? (
            <p className="text-[13px] leading-snug text-muted">{t('p.businesses.noPlansBody')}</p>
          ) : (
            <Select<PlanCode>
              id="new-business-plan"
              value={chosenPlan}
              onChange={setPlanCode}
              options={sellablePlans.map((plan) => ({ value: plan.code, label: plan.name }))}
            />
          )}
        </Field>
        <Field
          label={t('p.businesses.trialDaysLabel')}
          hint={t('p.businesses.trialDaysHint')}
          htmlFor="new-business-trial"
        >
          <TextInput id="new-business-trial" value={trialDays} onChange={setTrialDays} />
        </Field>
      </Modal>

      {/* The one time this password is readable. It is never fetched again. */}
      <Modal
        open={firstPassword !== null}
        onClose={() => setFirstPassword(null)}
        title={t('p.businesses.passwordTitle')}
        subtitle={firstPassword?.business}
        footer={
          <>
            <Button icon={<Copy className="size-4" />} onClick={copyPassword}>
              {t('act.copy')}
            </Button>
            <Button variant="primary" onClick={() => setFirstPassword(null)}>
              {t('act.close')}
            </Button>
          </>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-sub">
          {t('p.businesses.passwordBody', { owner: firstPassword?.owner ?? '' })}
        </p>
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-r border border-glass-border bg-glass p-3 text-sm">
          <dt className="text-muted">{t('p.businesses.signInBusiness')}</dt>
          <dd className="font-display font-bold text-dark">{firstPassword?.slug}</dd>
          <dt className="text-muted">{t('label.username')}</dt>
          <dd className="font-display font-bold text-dark">{firstPassword?.username}</dd>
          <dt className="text-muted">{t('auth.password')}</dt>
          <dd className="font-display font-bold tracking-[1px] text-dark">{firstPassword?.password}</dd>
        </dl>
      </Modal>
    </>
  )
}
